import React, { FC, useEffect, type SVGProps } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { enqueueSnackbar } from 'notistack';
import { useQueryClient } from 'react-query';
import ReactPopup from 'reactjs-popup';
import { LuCheck, LuX } from 'react-icons/lu';
import { useParams } from 'react-router-dom';

import { useManageSetActivationSignalStatusModalStore } from './ManageSetActivationSignalStatusModalStore';

import useSetActivationSignalStatus from 'hooks/Traps/useSetActivationSignalStatus';
import { SetActivationSignalFormValues, setActivationSignalStatusSchema, StatusTypes } from 'api/traps/types';
import { MLInput } from 'components/Input/Input';
import { useGetTrapDetail } from 'hooks/Traps/Trap/useGetTrapDetail';
// import useGetSRBMDetail from 'hooks/Traps/SRBMTraps/useGetSRBMDetail';

interface SetActivationSignalStatusModalProps {
  refetchActivationSignals: () => void;
  type: string;
}

const CheckIcon = LuCheck as React.ComponentType<SVGProps<SVGSVGElement>>;
const XIcon = LuX as React.ComponentType<SVGProps<SVGSVGElement>>;

const SetActivationSignalStatusModal: FC<SetActivationSignalStatusModalProps> = ({
  refetchActivationSignals,
  type,
}) => {
  const { slug } = useParams();
  const { putSetActivationSignalStatus } = useSetActivationSignalStatus();
  const { id, isOpen, defaultStatus, onManageSetActivationSignalStatusModalClose } =
    useManageSetActivationSignalStatusModalStore();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  // const useTrapDetail = (slug: string, type: string) => {
  //   const srbm = useGetSRBMDetail(slug || '');
  //   const trap = useGetTrapDetail(slug || '', type);

  //   const isSRBM = type === 'srbm';

  //   return isSRBM ? srbm : trap;
  // };

  const { refetch } = useGetTrapDetail(slug || '', type);

  const { handleSubmit, control, reset, watch } = useForm<SetActivationSignalFormValues>({
    resolver: zodResolver(setActivationSignalStatusSchema),
    defaultValues: {
      status: '',
      comment: '',
      type: type,
    },
  });
  const currentStatus = watch('status');

  useEffect(() => {
    if (!isOpen) return;

    reset({
      status: defaultStatus ?? '',
      comment: '',
      type,
    });
  }, [defaultStatus, isOpen, type, reset]);

  const onPutSetActivationSignalStatus = (data: SetActivationSignalFormValues) => {
    putSetActivationSignalStatus(
      { statusData: data, id: id || '' },
      {
        onSuccess: () => {
          enqueueSnackbar('Activation signal status was updated successfully', { variant: 'success' });
          refetchActivationSignals();
          refetch();
          queryClient.invalidateQueries({ queryKey: 'MCTDetailChartData' });
          onManageSetActivationSignalStatusModalClose();
        },
      },
    );
  };

  return (
    <Modal isOpen={isOpen} toggle={onManageSetActivationSignalStatusModalClose} size="md">
      <ModalHeader className="border-bottom pb-2 pt-3 mx-3" toggle={onManageSetActivationSignalStatusModalClose}>
        {t('MCTDetail.TrapHistoryCard.modal.header')}
      </ModalHeader>
      <ModalBody>
        <div className="row m-2">
          <div className="col-xl-12">
            <Controller
              control={control}
              name="status"
              render={({ field, fieldState: { error } }) => (
                <div className="col-md-12">
                  <label className={`form-label mb-1 ${error ? 'text-danger' : ''}`}>
                    {t('MCTDetail.TrapHistoryCard.modal.selectStatus')}
                  </label>
                  <select {...field} className={`form-control ${error ? 'is-invalid border-danger' : ''}`} required>
                    <option value="">{t('MCTDetail.TrapHistoryCard.modal.selectStatus')}</option>
                    {type !== 'srbm' && (
                      <option value={StatusTypes.APPROVED_MICE}>
                        {t('MCTDetail.TrapHistoryCard.modal.approveMice')}
                      </option>
                    )}
                    {type !== 'srbm' && (
                      <option value={StatusTypes.CAUGHT_EMPTY}>
                        {t('MCTDetail.TrapHistoryCard.modal.captureEmpty')}
                      </option>
                    )}
                    {type === 'srbm' && (
                      <option value={StatusTypes.ACTIVITY}>{t('MCTDetail.TrapHistoryCard.modal.Activity')}</option>
                    )}
                    {type === 'srbm' && (
                      <option value={StatusTypes.CONSUMPTION}>
                        {t('MCTDetail.TrapHistoryCard.modal.Consumption')}
                      </option>
                    )}
                    <option value={StatusTypes.APPROVED_NOT_MICE}>
                      {t('MCTDetail.TrapHistoryCard.modal.approveNotMice')}
                    </option>
                    <option value={StatusTypes.REJECTED}>{t('MCTDetail.TrapHistoryCard.modal.reject')}</option>
                  </select>
                  {error && <div className="text-danger w-100 d-block mt-1">{error?.message}</div>}
                </div>
              )}
            />
            {currentStatus === StatusTypes.APPROVED_NOT_MICE && (
              <p className="text-muted py-1">{t('MCTDetail.TrapHistoryCard.modal.notMiceApproved')}</p>
            )}
          </div>
        </div>
        <div className="row m-2">
          <div className="col-xl-12">
            <Controller
              control={control}
              name="comment"
              render={({ field, fieldState: { error } }) => (
                <MLInput
                  type="text"
                  label={t('MCTDetail.TrapHistoryCard.modal.comment')}
                  error={error?.message}
                  {...field}
                />
              )}
            />
            <p className="text-muted fs-12 py-1">{t('MCTDetail.TrapHistoryCard.modal.hintModal')}</p>
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          className="btn btn-ghost-danger"
          onClick={() => {
            onManageSetActivationSignalStatusModalClose();
          }}
        >
          {t('Modals.delete-modal.cancel')}
        </Button>{' '}
        <ReactPopup
          trigger={
            <button type="button" className="btn btn-ghost-success">
              {t('MCTDetail.TrapHistoryCard.modal.Save')}
            </button>
          }
          position="top center"
          arrow={false}
          modal
          // ось тут прокидуємо стиль із достатнім zIndex:
          contentStyle={{ zIndex: 2000, position: 'relative' }}
          overlayStyle={{ zIndex: 1999, background: 'transparent' }}
        >
          <div className="alert alert-danger mb-xl-0 d-flex align-items-center" role="alert">
            <i className="ri-error-warning-line me-3 align-middle" />
            {t('MCTDetail.TrapHistoryCard.modal.Status')}{' '}
            <strong>&nbsp;{t('MCTDetail.TrapHistoryCard.modal.Cannot')}&nbsp;</strong>{' '}
            {t('MCTDetail.TrapHistoryCard.modal.ChangeAfter')}
            <div>
              <Button
                className="btn  btn-sm btn-ghost-danger"
                onClick={() => {
                  onManageSetActivationSignalStatusModalClose();
                }}
              >
                <XIcon fontSize={24} />
              </Button>
              <Button className="btn btn-sm btn-ghost-success" onClick={handleSubmit(onPutSetActivationSignalStatus)}>
                <CheckIcon fontSize={24} />
              </Button>
            </div>
          </div>
        </ReactPopup>
      </ModalFooter>
    </Modal>
  );
};

export default SetActivationSignalStatusModal;
